
/**
 * Push Notification Service
 * Handles client-side push notification management
 * 
 * Uses inline authentication helpers to avoid import issues
 */
class PushNotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.apiBaseUrl = process.env.REACT_APP_BASE_URL || '';
  }

  /**
   * Get user data from localStorage 'me' object
   * @returns {Object|null}
   */
  getMe() {
    try {
      const meStr = localStorage.getItem('me');
      if (!meStr) return null;
      
      const me = JSON.parse(meStr);
      if (!me || (!me.id && !me.userid && !me.userId)) {
        return null;
      }
      
      return me;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return null;
    }
  }

  /**
   * Get current user ID
   * @returns {number|null}
   */
  getCurrentUserId() {
    const me = this.getMe();
    if (me) {
      return Number(me.id || me.userid || me.userId) || null;
    }
    
    // Fallback to window.__ME__ if available
    if (window.__ME__) {
      return Number(window.__ME__.id || window.__ME__.userid || window.__ME__.userId) || null;
    }
    
    return null;
  }

  /**
   * Get current tenant ID
   * @returns {number|null}
   */
  getCurrentTenantId() {
    const me = this.getMe();
    if (me) {
      return Number(me.tenantId || me.tenantid || me.tenant_id) || null;
    }
    
    // Fallback to window.__ME__ if available
    if (window.__ME__) {
      return Number(window.__ME__.tenantId || window.__ME__.tenantid || window.__ME__.tenant_id) || null;
    }
    
    return null;
  }

  /**
   * Get authentication headers for API requests
   * @returns {Object} Headers object with authentication
   */
  getAuthHeaders() {
    const userId = this.getCurrentUserId();
    const tenantId = this.getCurrentTenantId();
    
    const headers = {
      'Content-Type': 'application/json'
    };

    // Add user and tenant headers
    if (userId) {
      headers['x-user-id'] = userId.toString();
    }
    if (tenantId) {
      headers['x-tenant-id'] = tenantId.toString();
    }

    // Also check for token if available (fallback)
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    const userId = this.getCurrentUserId();
    const tenantId = this.getCurrentTenantId();
    return !!(userId && tenantId);
  }

  /**
   * Initialize the service and check notification support
   * @returns {Promise<boolean>} Whether notifications are supported
   */
  async initialize() {
    try {
      // Check authentication first
      if (!this.isAuthenticated()) {
        console.warn('User not authenticated - push notifications require login');
        return false;
      }

      // Check if service workers are supported
      if (!('serviceWorker' in navigator)) {
        console.warn('Service Workers are not supported');
        return false;
      }

      // Check if push notifications are supported
      if (!('PushManager' in window)) {
        console.warn('Push notifications are not supported');
        return false;
      }

      // Check if notifications API is supported
      if (!('Notification' in window)) {
        console.warn('Notifications API is not supported');
        return false;
      }

      // Wait for service worker to be ready
      this.registration = await navigator.serviceWorker.ready;
      console.log('✅ Push notification service initialized');
      
      return true;
    } catch (error) {
      console.error('Error initializing push notification service:', error);
      return false;
    }
  }

  /**
   * Check if notifications are supported in this browser
   * @returns {boolean}
   */
  isSupported() {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Get current notification permission status
   * @returns {string} 'granted', 'denied', or 'default'
   */
  getPermissionStatus() {
    if (!('Notification' in window)) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   * @returns {Promise<string>} Permission result ('granted', 'denied', or 'default')
   */
  async requestPermission() {
    try {
      if (!('Notification' in window)) {
        throw new Error('Notifications are not supported');
      }

      const permission = await Notification.requestPermission();
      console.log(`Notification permission: ${permission}`);
      
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw error;
    }
  }

  /**
   * Get VAPID public key from server
   * @returns {Promise<string>} VAPID public key
   */
  async getVapidPublicKey() {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/notifications/push/vapid-public-key`, {
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get VAPID key: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success || !data.publicKey) {
        throw new Error('Invalid response from server');
      }

      return data.publicKey;
    } catch (error) {
      console.error('Error getting VAPID public key:', error);
      throw error;
    }
  }

  /**
   * Subscribe to push notifications
   * @returns {Promise<PushSubscription>}
   */
  async subscribe() {
    try {
      // Check authentication
      if (!this.isAuthenticated()) {
        throw new Error('User must be authenticated to subscribe to push notifications');
      }

      // Ensure initialized
      if (!this.registration) {
        const initialized = await this.initialize();
        if (!initialized) {
          throw new Error('Failed to initialize push notification service');
        }
      }

      // Request permission
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission not granted');
      }

      // Get VAPID public key
      const vapidPublicKey = await this.getVapidPublicKey();

      // Convert VAPID key to Uint8Array
      const convertedKey = this.urlBase64ToUint8Array(vapidPublicKey);

      // Subscribe to push notifications
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey
      });

      console.log('✅ Push subscription created:', this.subscription);

      // Send subscription to server
      await this.saveSubscriptionToServer(this.subscription);

      return this.subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   * @returns {Promise<boolean>} Whether unsubscribe was successful
   */
  async unsubscribe() {
    try {
      // Get current subscription
      const subscription = await this.getSubscription();
      
      if (!subscription) {
        console.log('No active subscription to unsubscribe from');
        return true;
      }

      // Remove subscription from server
      await this.removeSubscriptionFromServer(subscription);

      // Unsubscribe from push manager
      const success = await subscription.unsubscribe();
      
      if (success) {
        this.subscription = null;
        console.log('✅ Successfully unsubscribed from push notifications');
      }

      return success;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      throw error;
    }
  }

  /**
   * Get current push subscription
   * @returns {Promise<PushSubscription|null>}
   */
  async getSubscription() {
    try {
      if (!this.registration) {
        await this.initialize();
      }

      if (!this.registration) {
        return null;
      }

      this.subscription = await this.registration.pushManager.getSubscription();
      return this.subscription;
    } catch (error) {
      console.error('Error getting push subscription:', error);
      return null;
    }
  }

  /**
   * Check if currently subscribed to push notifications
   * @returns {Promise<boolean>}
   */
  async isSubscribed() {
    try {
      const subscription = await this.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Save subscription to server
   * @param {PushSubscription} subscription
   * @returns {Promise<void>}
   */
  async saveSubscriptionToServer(subscription) {
    try {
      // Check authentication
      if (!this.isAuthenticated()) {
        throw new Error('User must be authenticated');
      }

      const response = await fetch(`${this.apiBaseUrl}/api/notifications/push/subscribe`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save subscription: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to save subscription');
      }

      console.log('✅ Subscription saved to server');
    } catch (error) {
      console.error('Error saving subscription to server:', error);
      throw error;
    }
  }

  /**
   * Remove subscription from server
   * @param {PushSubscription} subscription
   * @returns {Promise<void>}
   */
  async removeSubscriptionFromServer(subscription) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/notifications/push/unsubscribe`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to remove subscription: ${response.status}`);
      }

      console.log('✅ Subscription removed from server');
    } catch (error) {
      console.error('Error removing subscription from server:', error);
      throw error;
    }
  }

  /**
   * Check subscription status on server
   * @returns {Promise<Object>} Status object
   */
  async checkStatus() {
    try {
      if (!this.isAuthenticated()) {
        return {
          isEnabled: false,
          hasSubscription: false,
          error: 'Not authenticated'
        };
      }

      const response = await fetch(`${this.apiBaseUrl}/api/notifications/push/status`, {
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Status check failed: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking push notification status:', error);
      return {
        isEnabled: false,
        hasSubscription: false,
        error: error.message
      };
    }
  }

  /**
   * Send a test notification
   * @returns {Promise<boolean>}
   */
  async sendTestNotification() {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('User must be authenticated');
      }

      const response = await fetch(`${this.apiBaseUrl}/api/notifications/push/test`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Test notification failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Test notification sent');
      return data.success;
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  }

  /**
   * Convert base64 URL to Uint8Array
   * @param {string} base64String
   * @returns {Uint8Array}
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// Export singleton instance
const pushNotificationService = new PushNotificationService();
export default pushNotificationService;